import axios from "axios";
import { useEffect } from "react";

// Zoho Desk ASAP help widget ("PromoLink Support"). The user is signed in silently with a
// short-lived JWT from /api/support/zoho-token; Zoho's floating launcher stays hidden and the
// sidebar's Support action opens the widget.
const SCRIPT_ID = "zohodeskasapscript";
const SCRIPT_SRC =
  "https://desk.zoho.com/portal/api/web/asapApp/1488983000000580054?orgId=939538052";

// Email the widget is signed in as, so the next Portal user on this browser can be told apart
// even when the previous session expired without a Portal logout.
const WIDGET_USER_KEY = "zohoDeskUser";

const readWidgetUser = () => {
  try {
    return window.localStorage.getItem(WIDGET_USER_KEY);
  } catch {
    return null;
  }
};

const writeWidgetUser = (email) => {
  try {
    if (email) window.localStorage.setItem(WIDGET_USER_KEY, email);
    else window.localStorage.removeItem(WIDGET_USER_KEY);
  } catch {
    // Storage blocked: the stale-session guard just won't fire.
  }
};

// Zoho's embed code, same behaviour (no CSP on the Portal, so no nonce): callbacks queue until
// the widget script sets ZohoDeskAsapReadyStatus, then run immediately.
const loadZohoDesk = () => {
  if (!window.ZohoDeskAsapReady) {
    window.ZohoDeskAsapReady = function (callback) {
      const calls = (window.ZohoDeskAsap__asyncalls = window.ZohoDeskAsap__asyncalls || []);
      if (callback) calls.push(callback);
      if (window.ZohoDeskAsapReadyStatus) {
        calls.forEach((call) => call && call());
        window.ZohoDeskAsap__asyncalls = null;
      }
    };
  }

  if (document.getElementById(SCRIPT_ID)) return;

  // No Zoho floating launcher: the sidebar Support item is the only way in. The SDK reads
  // ZohoDeskAsapInit() at start-up and never shows the button when hideLauncherIcon is set
  // (undocumented, so the CSS rule backs it up; it beats the inline display the SDK sets).
  window.ZohoDeskAsapInit = () => ({ hideLauncherIcon: true });
  const style = document.createElement("style");
  style.textContent = "#zohohc-asap-web-button { display: none !important; }";
  document.head.appendChild(style);

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.id = SCRIPT_ID;
  script.defer = true;
  script.src = SCRIPT_SRC;
  const first = document.getElementsByTagName("script")[0];
  first.parentNode.insertBefore(script, first);
};

let initializedFor = null;

/** Loads the widget and signs the current Portal user in to it. Call once inside the signed-in layout. */
export const useZohoDeskWidget = (user) => {
  const email = user?.email;
  const enabled = user?.supportWidgetEnabled === true;

  useEffect(() => {
    if (!enabled || !email || initializedFor === email) return;
    initializedFor = email;

    loadZohoDesk();
    window.ZohoDeskAsapReady(() => {
      const asap = window.ZohoDeskAsap;
      let loginAfterLogout = false;

      const signIn = () =>
        asap.invoke("login", (successCallback, failureCallback) => {
          // axios, so the selected customer (accountId header) goes along for the contact sync.
          axios
            .get("/api/support/zoho-token", { responseType: "text" })
            .then((response) => successCallback(response.data))
            .catch(() => failureCallback());
        });

      asap.on("onAfterUserLoggedIn", () => writeWidgetUser(email));
      asap.on("onAfterUserLoggedOut", () => {
        writeWidgetUser(null);
        if (loginAfterLogout) {
          loginAfterLogout = false;
          signIn();
        }
      });

      const previous = readWidgetUser();
      if (previous && previous.toLowerCase() !== email.toLowerCase()) {
        // Someone else's widget session is still alive in this browser: end it first.
        loginAfterLogout = true;
        asap.invoke("logout");
        setTimeout(() => {
          if (loginAfterLogout) {
            loginAfterLogout = false;
            signIn();
          }
        }, 3000);
      } else {
        signIn();
      }

      asap.invoke("hide", "app.launcher");
    });
  }, [email, enabled]);
};

/** Opens the widget (sidebar Support action). */
export const openSupport = () => {
  if (!window.ZohoDeskAsapReady) return;
  window.ZohoDeskAsapReady(() => window.ZohoDeskAsap.invoke("open"));
};

/**
 * Signs the widget out before the Portal logout redirect, so the next person on this browser
 * doesn't see the previous user's tickets. Resolves on Zoho's logged-out event or after the
 * timeout, and at once when the widget was never loaded.
 */
export const logoutZohoDesk = (timeoutMs = 1500) => {
  if (!window.ZohoDeskAsapReady || !document.getElementById(SCRIPT_ID)) return Promise.resolve();

  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    window.ZohoDeskAsapReady(() => {
      window.ZohoDeskAsap.on("onAfterUserLoggedOut", () => {
        clearTimeout(timer);
        resolve();
      });
      window.ZohoDeskAsap.invoke("logout");
    });
  });
};
